/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import TextureDataHandler from "App/utils/textures/texture_data_handler";
import ImageDataLoader from "App/utils/textures/image_data_loader";
import {logInfo} from "App/debug";
import {EDataTypeType} from "App/geometry/material/texture";
import ImageDataLoadersFactory from "App/utils/textures/image_data_loaders_factory";

export default
class DDSImageDataLoader extends ImageDataLoader {

    public static TypeName: string = EDataTypeType.ddsImageTexture;

    constructor(aTextureDataHandler: TextureDataHandler, aDiffuseTextureProperties: any, aLevel: number) {
        super(aTextureDataHandler, aDiffuseTextureProperties, aLevel);
    }

    public static create(aTextureDataHandler: TextureDataHandler, aDiffuseTextureProperties: any, aLevel: number): DDSImageDataLoader {
        return new DDSImageDataLoader(aTextureDataHandler, aDiffuseTextureProperties, aLevel);
    }

    public static getSizeFromResolution(aWidth: number, aHeight: number, aFormat: string): number {
        let blockBytes = 8;
        switch (aFormat) {
            case "dxt1":
                blockBytes = 8;
                break;
            case "dxt3":
                blockBytes = 16;
                break;
            case "dxt5":
                blockBytes = 16;
                break;
        }

        return Math.max(4, aWidth) / 4 * Math.max(4, aHeight) / 4 * blockBytes;
    }

    private parseBuffer(aBuffer: Uint8Array): any {

        // the bytOffset is offset in the ArrayBuffer in Uint8Array as the input buffer represents the textures batch
        let buffer = new DataView(aBuffer.buffer, aBuffer.byteOffset);

        let pos: number = 0;
        let magic = buffer.getInt32(pos, true); pos += 4;
        let size = buffer.getInt32(pos, true); pos += 4;
        let flags = buffer.getInt32(pos, true); pos += 4;
        let height = buffer.getInt32(pos, true); pos += 4;
        let width = buffer.getInt32(pos, true); pos += 4;
        let pitchOrLin = buffer.getInt32(pos, true); pos += 4;
        let depth = buffer.getInt32(pos, true); pos += 4;
        let mipmapCount = buffer.getInt32(pos, true); pos += 4;

        let reserved = new Int32Array(11);
        for (let i = 0; i < 11; i++) {
            reserved[i] = buffer.getInt32(pos, true);pos += 4;
        }
        let pixelformat = new Int32Array(8);
        for (let i = 0; i < 8; i++) {
            pixelformat[i] = buffer.getInt32(pos, true);pos += 4;
        }
        let caps: number = buffer.getInt32(pos, true); pos += 4;// fourCC
        let caps2 = buffer.getInt32(pos, true); pos += 4;
        let caps3 = buffer.getInt32(pos, true); pos += 4;
        let caps4 = buffer.getInt32(pos, true); pos += 4;
        let reserved2 = buffer.getInt32(pos, true); pos += 4;

        let DDS_MAGIC = 0x20534444;
        if (magic !== DDS_MAGIC) {
            logInfo('Error when parsing dds file: Invalid magic number in DDS header');
            return;
        }

        let DDPF_FOURCC: number = 0x4;
        if ((caps & DDPF_FOURCC) === DDPF_FOURCC) {
            logInfo('Error when parsing dds file: Unsupported format, must contain a FourCC code');
            return;
        }

        let fourCCToInt32 = function(value) {
            return value.charCodeAt(0) +
                (value.charCodeAt(1) << 8) +
                (value.charCodeAt(2) << 16) +
                (value.charCodeAt(3) << 24);
        };

        let blockBytes;
        let format;
        let fourCC = pixelformat[2];
        let DXT1 = fourCCToInt32('DXT1');
        let DXT3 = fourCCToInt32('DXT3');
        let DXT5 = fourCCToInt32('DXT5');
        let _hasAlpha = false;
        switch (fourCC) {
            case DXT1:
                blockBytes = 8;
                format = 'dxt1';
                break;
            case DXT3:
                blockBytes = 16;
                format = 'dxt3';
                _hasAlpha = true;
                break;
            case DXT5:
                blockBytes = 16;
                format = 'dxt5';
                _hasAlpha = true;
                break;
            default:
                logInfo('Error when parsing ds file: Unsupported FourCC code.');
        }

        let DDSD_MIPMAPCOUNT: number = 0x20000;
        if (flags & DDSD_MIPMAPCOUNT) {
            mipmapCount = Math.max(1, mipmapCount);
        }

        let dds = {
            mipmaps: [],
            width: width,
            height: height,
            format: format,
            hasAlpha: _hasAlpha,
            bpp: 4,
            mipmapCount: mipmapCount,
            isCubemap: false
        };

        let dataSize = 0;
        for (let mipLevel = 0; mipLevel < mipmapCount; mipLevel++) {
            dataSize = Math.max(4, width) / 4 * Math.max(4, height) / 4 * blockBytes;

            let data = new Uint8Array(dataSize);//buffer.buffer.slice(pos, pos + dataSize));
            data.set(new Uint8Array(buffer.buffer, buffer.byteOffset + pos, dataSize));
            pos += dataSize;

            let mipmap: any = {
                data: data,
                width: Math.max(1, width),
                height: Math.max(1, height)
            };

            dds.mipmaps[mipLevel] = mipmap;
            width = Math.floor(width / 2);
            height = Math.floor(height / 2);
        }

        return dds;
    }

    public prepareData() {
        if (this._loadIterator >= this.tiles.length) {
            return;
        }

        let tile = this.tiles[this._loadIterator];

        // we need only init the width and height here
        tile.width = this._diffuseTextureProperties.width;
        tile.height = this._diffuseTextureProperties.height;

        tile.data = this.parseBuffer(tile.compressedUint8Data);

        // here we test if we specify the texture using only the url wihtout knowing the size of it
        // it this case we re-init the properties based on th reloaded dom image object
        if (!this._diffuseTextureProperties.tiled &&
            this._diffuseTextureProperties.width === 0 &&
            this._diffuseTextureProperties.height === 0) {
            tile.width = tile.data.width;
            tile.height = tile.data.height;
            this._diffuseTextureProperties.width = tile.width;
            this._diffuseTextureProperties.height = tile.height;
        }

        tile.ready = true;

        // force the alpha according to the format
        this._diffuseTextureProperties.alpha = tile.data.hasAlpha;

        this._loadIterator++;
    };
}

ImageDataLoadersFactory.register(DDSImageDataLoader);